import { Direction, MazePiece } from '../types/maze';

const DIRS: { dir: Direction; dr: number; dc: number }[] = [
  { dir: 'up', dr: -1, dc: 0 },
  { dir: 'down', dr: 1, dc: 0 },
  { dir: 'left', dr: 0, dc: -1 },
  { dir: 'right', dr: 0, dc: 1 },
];

function key(r: number, c: number) {
  return `${r},${c}`;
}

export interface GeneratedMaze {
  rows: number;
  cols: number;
  pieces: MazePiece[];
  cellPieceId: number[][];
}

function getFreeNeighbors(
  cur: { row: number; col: number },
  rows: number,
  cols: number,
  remaining: Set<string>,
  used: Set<string>
): { row: number; col: number }[] {
  const result: { row: number; col: number }[] = [];
  for (const { dr, dc } of DIRS) {
    const nr = cur.row + dr;
    const nc = cur.col + dc;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && remaining.has(key(nr, nc)) && !used.has(key(nr, nc))) {
      result.push({ row: nr, col: nc });
    }
  }
  return result;
}

export function generateMaze(rows: number, cols: number, maxPieceLen: number): GeneratedMaze {
  const cleared = new Set<string>();
  const remaining = new Set<string>();
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) remaining.add(key(r, c));

  const cellPieceId: number[][] = Array.from({ length: rows }, () => Array(cols).fill(-1));
  const pieces: MazePiece[] = [];
  let pieceId = 0;

  function isClearLine(row: number, col: number, dir: Direction): boolean {
    const { dr, dc } = DIRS.find((d) => d.dir === dir)!;
    let r = row + dr;
    let c = col + dc;
    while (r >= 0 && r < rows && c >= 0 && c < cols) {
      if (!cleared.has(key(r, c))) return false;
      r += dr;
      c += dc;
    }
    return true;
  }

  while (remaining.size > 0) {
    const eligible: { row: number; col: number; dirs: Direction[] }[] = [];
    remaining.forEach((k) => {
      const [r, c] = k.split(',').map(Number);
      const dirs = DIRS.filter(({ dir }) => isClearLine(r, c, dir)).map((d) => d.dir);
      if (dirs.length > 0) eligible.push({ row: r, col: c, dirs });
    });

    const head = eligible[Math.floor(Math.random() * eligible.length)];
    const direction = head.dirs[Math.floor(Math.random() * head.dirs.length)];

 
    const minLen = Math.max(2, Math.floor(maxPieceLen * 0.65));
    const targetLen = minLen + Math.floor(Math.random() * (maxPieceLen - minLen + 1));
    const cells: { row: number; col: number }[] = [{ row: head.row, col: head.col }];
    const used = new Set<string>([key(head.row, head.col)]);
    let cur = { row: head.row, col: head.col };

    for (let step = 1; step < targetLen; step++) {
      const neighbors = getFreeNeighbors(cur, rows, cols, remaining, used);
      if (neighbors.length === 0) break;
      const next = neighbors[Math.floor(Math.random() * neighbors.length)];
      cells.push(next);
      used.add(key(next.row, next.col));
      cur = next;
    }

    for (const { row, col } of cells) {
      remaining.delete(key(row, col));
      cleared.add(key(row, col));
      cellPieceId[row][col] = pieceId;
    }

    pieces.push({ id: pieceId, headCell: { row: head.row, col: head.col }, cells, direction, active: true });
    pieceId++;
  }

  return { rows, cols, pieces, cellPieceId };
}

export function isPieceClearable(maze: GeneratedMaze, piece: MazePiece): boolean {
  if (!piece.active) return false;
  const { dr, dc } = DIRS.find((d) => d.dir === piece.direction)!;
  let r = piece.headCell.row + dr;
  let c = piece.headCell.col + dc;
  while (r >= 0 && r < maze.rows && c >= 0 && c < maze.cols) {
    const pid = maze.cellPieceId[r][c];
    if (pid !== -1 && maze.pieces[pid].active) return false;
    r += dr;
    c += dc;
  }
  return true;
}

export function findHintPiece(maze: GeneratedMaze): MazePiece | null {
  return maze.pieces.find((p) => p.active && isPieceClearable(maze, p)) || null;
}

export function isMazeSolved(maze: GeneratedMaze): boolean {
  return maze.pieces.every((p) => !p.active);
}